import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import useRealtimeNotifications from '../../hooks/useRealtimeNotifications';
import EmptyState from '../../components/ui/EmptyState';

// ─── Hub Sections ─────────────────────────────────────────────────────────────

const HUB_SECTIONS = [
  {
    id: 'all',
    label: 'Tout',
    icon: 'LayoutGrid',
    types: null,
    color: 'brand-earth',
    gradient: 'from-amber-700/10 to-amber-900/5',
    badge: 'bg-amber-700',
    accent: 'text-amber-800',
    border: 'border-amber-700/30',
    activeBg: 'bg-amber-700/10',
  },
  {
    id: 'appointments',
    label: 'Rendez-vous',
    icon: 'CalendarClock',
    types: ['appointment_confirmation', 'appointment', 'notary_appointment', 'booking_update'],
    color: 'blue',
    gradient: 'from-blue-500/10 to-blue-700/5',
    badge: 'bg-blue-600',
    accent: 'text-blue-700',
    border: 'border-blue-400/30',
    activeBg: 'bg-blue-50',
  },
  {
    id: 'price_drops',
    label: 'Baisses de prix',
    icon: 'TrendingDown',
    types: ['property_update', 'document_ready'],
    color: 'emerald',
    gradient: 'from-emerald-500/10 to-emerald-700/5',
    badge: 'bg-emerald-600',
    accent: 'text-emerald-700',
    border: 'border-emerald-400/30',
    activeBg: 'bg-emerald-50',
  },
  {
    id: 'inquiries',
    label: 'Demandes',
    icon: 'MessageSquareDot',
    types: ['property_inquiry', 'general'],
    color: 'violet',
    gradient: 'from-violet-500/10 to-violet-700/5',
    badge: 'bg-violet-600',
    accent: 'text-violet-700',
    border: 'border-violet-400/30',
    activeBg: 'bg-violet-50',
  },
  {
    id: 'leads',
    label: 'Scoring Leads',
    icon: 'Zap',
    types: ['property_alert', 'security_alert'],
    color: 'orange',
    gradient: 'from-orange-500/10 to-orange-700/5',
    badge: 'bg-orange-600',
    accent: 'text-orange-700',
    border: 'border-orange-400/30',
    activeBg: 'bg-orange-50',
  },
];

// ─── Type metadata with rich action buttons ───────────────────────────────────

const TYPE_META = {
  appointment_confirmation: {
    label: 'Confirmation RDV',
    icon: 'CalendarCheck',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
    leftBorder: 'border-l-blue-500',
    actions: [
      { label: 'Voir le RDV', icon: 'CalendarDays', path: '/appointments', variant: 'primary' },
      { label: 'Reprogrammer', icon: 'RefreshCw', path: '/reschedule', variant: 'outline' },
    ],
  },
  appointment: {
    label: 'Rendez-vous',
    icon: 'Calendar',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
    leftBorder: 'border-l-blue-400',
    actions: [
      { label: 'Gérer', icon: 'Settings2', path: '/appointments', variant: 'primary' },
      { label: 'Messagerie', icon: 'MessageSquare', path: '/in-app-messaging', variant: 'outline' },
    ],
  },
  notary_appointment: {
    label: 'RDV Notaire',
    icon: 'Scale',
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    border: 'border-purple-200',
    leftBorder: 'border-l-purple-500',
    actions: [
      { label: 'Voir le RDV', icon: 'CalendarDays', path: '/notary-booking', variant: 'primary' },
      { label: 'Services légaux', icon: 'FileText', path: '/legal-services-center', variant: 'outline' },
    ],
  },
  booking_update: {
    label: 'Réservation',
    icon: 'BookOpen',
    bg: 'bg-sky-50',
    text: 'text-sky-700',
    border: 'border-sky-200',
    leftBorder: 'border-l-sky-400',
    actions: [
      { label: 'Voir la réservation', icon: 'Eye', path: '/appointments', variant: 'primary' },
    ],
  },
  property_inquiry: {
    label: 'Demande reçue',
    icon: 'Home',
    bg: 'bg-violet-50',
    text: 'text-violet-700',
    border: 'border-violet-200',
    leftBorder: 'border-l-violet-500',
    actions: [
      { label: 'Voir la propriété', icon: 'Building2', path: '/property-search-listings', variant: 'primary' },
      { label: 'Répondre', icon: 'MessageSquare', path: '/in-app-messaging', variant: 'outline' },
      { label: 'Planifier visite', icon: 'CalendarPlus', path: '/consultation-booking-hub', variant: 'ghost' },
    ],
  },
  property_update: {
    label: 'Baisse de prix',
    icon: 'TrendingDown',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200',
    leftBorder: 'border-l-emerald-500',
    actions: [
      { label: 'Voir le bien', icon: 'Eye', path: '/property-search-listings', variant: 'primary' },
      { label: 'Calculer ROI', icon: 'Calculator', path: '/roi-calculator-tool', variant: 'outline' },
      { label: 'Analyser marché', icon: 'BarChart2', path: '/market-analysis-dashboard', variant: 'ghost' },
    ],
  },
  property_alert: {
    label: 'Alerte scoring',
    icon: 'Zap',
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    border: 'border-orange-200',
    leftBorder: 'border-l-orange-500',
    actions: [
      { label: 'Voir les biens', icon: 'Building2', path: '/property-search-listings', variant: 'primary' },
      { label: 'Watchlist', icon: 'Bookmark', path: '/investor-watchlist', variant: 'outline' },
    ],
  },
  document_ready: {
    label: 'Document prêt',
    icon: 'FileText',
    bg: 'bg-indigo-50',
    text: 'text-indigo-700',
    border: 'border-indigo-200',
    leftBorder: 'border-l-indigo-400',
    actions: [
      { label: 'Télécharger', icon: 'Download', path: '/account-dashboard', variant: 'primary' },
    ],
  },
  security_alert: {
    label: 'Alerte sécurité',
    icon: 'ShieldAlert',
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-200',
    leftBorder: 'border-l-red-500',
    actions: [
      { label: 'Vérifier', icon: 'ShieldCheck', path: '/user-profile', variant: 'primary' },
    ],
  },
  general: {
    label: 'Général',
    icon: 'Bell',
    bg: 'bg-gray-50',
    text: 'text-gray-600',
    border: 'border-gray-200',
    leftBorder: 'border-l-gray-300',
    actions: [
      { label: 'Voir', icon: 'ArrowRight', path: '/notifications', variant: 'outline' },
    ],
  },
};

const getMeta = (type) => TYPE_META?.[type] || TYPE_META?.general;

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr)?.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'À l\'instant';
  if (mins < 60) return `Il y a ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Il y a ${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `Il y a ${days}j`;
  return new Date(dateStr)?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
};

// ─── Lead Score Badge ─────────────────────────────────────────────────────────

const LeadScoreBadge = ({ score }) => {
  if (!score) return null;
  const level = score >= 80 ? { label: 'Chaud', color: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' }
    : score >= 50 ? { label: 'Tiède', color: 'bg-orange-100 text-orange-700 border-orange-200', dot: 'bg-orange-500' }
    : { label: 'Froid', color: 'bg-blue-100 text-blue-700 border-blue-200', dot: 'bg-blue-400' };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${level?.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${level?.dot}`} />Lead {level?.label}· {score}
    </span>
  );
};

// ─── Rich Action Buttons ──────────────────────────────────────────────────────

const ActionButtons = ({ actions, notificationId, onMarkRead, isUnread }) => {
  const navigate = useNavigate();

  const handleAction = (path) => {
    if (isUnread) onMarkRead(notificationId);
    navigate(path);
  };

  return (
    <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-border/30">
      {actions?.map((action, i) => (
        <button
          key={i}
          onClick={() => handleAction(action?.path)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-150 ${
            action?.variant === 'primary' ?'bg-brand-earth text-white hover:bg-brand-earth/90 shadow-sm'
              : action?.variant === 'outline' ?'bg-white border border-border text-text-primary hover:border-brand-earth/40 hover:text-brand-earth' :'bg-transparent text-text-secondary hover:text-text-primary hover:bg-surface'
          }`}
        >
          <Icon name={action?.icon} size={12} strokeWidth={2} />
          {action?.label}
        </button>
      ))}
    </div>
  );
};

// ─── Notification Card ────────────────────────────────────────────────────────

const HubCard = ({ notification, isSelected, onSelect, onMarkRead, onDelete }) => {
  const meta = getMeta(notification?.type);
  const isUnread = !notification?.is_read;
  const metadata = notification?.metadata || {};
  const leadScore = metadata?.lead_score || metadata?.score || null;
  const priceDrop = metadata?.price_drop || metadata?.old_price ? {
    old: metadata?.old_price,
    new: metadata?.new_price,
    pct: metadata?.price_drop_pct,
  } : null;

  return (
    <div
      className={`group relative bg-white rounded-2xl border border-l-4 ${meta?.leftBorder} transition-all duration-200 hover:shadow-md ${
        isUnread
          ? 'border-border/80 shadow-sm ring-1 ring-brand-gold/20'
          : 'border-border/40 opacity-90'
      } ${isSelected ? 'ring-2 ring-brand-earth/30' : ''}`}
    >
      {/* Unread pulse dot */}
      {isUnread && (
        <span className="absolute top-4 right-4 w-2 h-2 rounded-full bg-brand-gold animate-pulse" />
      )}

      {/* Selection checkbox */}
      <div className="absolute top-4 left-4 z-10">
        <button
          onClick={() => onSelect(notification?.id)}
          className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-150 ${
            isSelected
              ? 'bg-brand-earth border-brand-earth' :'border-border bg-white opacity-0 group-hover:opacity-100'
          }`}
        >
          {isSelected && <Icon name="Check" size={11} color="white" strokeWidth={3} />}
        </button>
      </div>

      <div className="p-4 pl-12 pr-10">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${meta?.bg} ${meta?.border} border`}>
            <Icon name={meta?.icon} size={18} className={meta?.text} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                {/* Title row */}
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h3 className={`text-sm leading-snug ${isUnread ? 'font-semibold text-text-primary' : 'font-medium text-text-secondary'}`}>
                    {notification?.title || 'Notification'}
                  </h3>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-medium border ${meta?.bg} ${meta?.text} ${meta?.border}`}>
                    <Icon name={meta?.icon} size={9} />
                    {meta?.label}
                  </span>
                  {leadScore && <LeadScoreBadge score={leadScore} />}
                </div>

                {/* Message */}
                <p className={`text-xs leading-relaxed line-clamp-2 ${isUnread ? 'text-text-primary' : 'text-text-secondary'}`}>
                  {notification?.message || '—'}
                </p>

                {/* Price drop highlight */}
                {priceDrop?.old && priceDrop?.new && (
                  <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-xl">
                    <Icon name="TrendingDown" size={13} className="text-emerald-600" />
                    <span className="text-xs text-emerald-800 font-semibold">
                      {priceDrop?.old?.toLocaleString('fr-FR')} → {priceDrop?.new?.toLocaleString('fr-FR')} FCFA
                    </span>
                    {priceDrop?.pct && (
                      <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-md">
                        -{priceDrop?.pct}%
                      </span>
                    )}
                  </div>
                )}

                {/* Appointment reminder highlight */}
                {(notification?.type === 'appointment_confirmation' || notification?.type === 'appointment') && metadata?.appointment_date && (
                  <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-xl">
                    <Icon name="Clock" size={13} className="text-blue-600" />
                    <span className="text-xs text-blue-800 font-semibold">
                      {new Date(metadata?.appointment_date)?.toLocaleDateString('fr-FR', {
                        weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                      })}
                    </span>
                    {metadata?.location && (
                      <span className="text-[10px] text-blue-600 flex items-center gap-0.5">
                        <Icon name="MapPin" size={9} />
                        {metadata?.location}
                      </span>
                    )}
                  </div>
                )}

                {/* Timestamp */}
                <div className="flex items-center gap-1.5 mt-2">
                  <Icon name="Clock" size={10} className="text-text-secondary" />
                  <span className="text-[10px] text-text-secondary">{formatRelativeTime(notification?.created_at)}</span>
                </div>

                {/* Rich action buttons */}
                <ActionButtons
                  actions={meta?.actions}
                  notificationId={notification?.id}
                  onMarkRead={onMarkRead}
                  isUnread={isUnread}
                />
              </div>

              {/* Quick actions */}
              <div className="flex flex-col items-center gap-1.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-150 mt-0.5">
                {isUnread && (
                  <button
                    onClick={() => onMarkRead(notification?.id)}
                    title="Marquer comme lu"
                    className="w-7 h-7 rounded-lg bg-surface hover:bg-brand-earth/10 flex items-center justify-center transition-colors"
                  >
                    <Icon name="CheckCheck" size={13} className="text-text-secondary hover:text-brand-earth" />
                  </button>
                )}
                <button
                  onClick={() => onDelete(notification?.id)}
                  title="Supprimer"
                  className="w-7 h-7 rounded-lg bg-surface hover:bg-red-50 flex items-center justify-center transition-colors"
                >
                  <Icon name="Trash2" size={13} className="text-text-secondary hover:text-red-500" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Stats Bar ────────────────────────────────────────────────────────────────

const StatsBar = ({ notifications }) => {
  const unread = notifications?.filter(n => !n?.is_read)?.length || 0;
  const appointments = notifications?.filter(n => ['appointment_confirmation', 'appointment', 'notary_appointment', 'booking_update']?.includes(n?.type))?.length || 0;
  const priceDrops = notifications?.filter(n => n?.type === 'property_update')?.length || 0;
  const inquiries = notifications?.filter(n => ['property_inquiry', 'general']?.includes(n?.type))?.length || 0;

  const stats = [
    { label: 'Non lues', value: unread, icon: 'BellDot', color: 'text-brand-gold', bg: 'bg-amber-50' },
    { label: 'RDV', value: appointments, icon: 'CalendarClock', color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Baisses prix', value: priceDrops, icon: 'TrendingDown', color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Demandes', value: inquiries, icon: 'MessageSquareDot', color: 'text-violet-600', bg: 'bg-violet-50' },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
      {stats?.map((stat) => (
        <div key={stat?.label} className={`flex items-center gap-3 px-4 py-3 ${stat?.bg} rounded-xl border border-border/30`}>
          <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
            <Icon name={stat?.icon} size={16} className={stat?.color} />
          </div>
          <div>
            <p className="text-lg font-bold text-text-primary leading-none">{stat?.value}</p>
            <p className="text-[10px] text-text-secondary mt-0.5">{stat?.label}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Main Hub ─────────────────────────────────────────────────────────────────

const NotificationHub = () => {
  const { user } = useAuth();
  const { unreadCount, refreshCount } = useRealtimeNotifications(user);

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [liveCount, setLiveCount] = useState(0);
  const [sectionCounts, setSectionCounts] = useState({});
  const prevUnreadRef = useRef(0);

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchNotifications = useCallback(async () => {
    if (!user?.id) { setLoading(false); return; }
    try {
      setLoading(true);
      const { data, error } = await supabase
        ?.from('notifications')
        ?.select('*')
        ?.eq('recipient_id', user?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(200);
      if (error) throw error;
      const all = data || [];
      setNotifications(all);

      const counts = {};
      HUB_SECTIONS?.forEach((sec) => {
        if (sec?.id === 'all') {
          counts['all'] = all?.filter(n => !n?.is_read)?.length;
        } else {
          counts[sec?.id] = all?.filter(n => !n?.is_read && sec?.types?.includes(n?.type))?.length;
        }
      });
      setSectionCounts(counts);
      setLiveCount(counts?.['all'] || 0);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  useEffect(() => {
    if (unreadCount !== prevUnreadRef?.current) {
      prevUnreadRef.current = unreadCount;
      fetchNotifications();
    }
  }, [unreadCount, fetchNotifications]);

  // ── Filtered list ──────────────────────────────────────────────────────────

  const filtered = notifications?.filter((n) => {
    if (activeSection === 'all') return true;
    const sec = HUB_SECTIONS?.find(s => s?.id === activeSection);
    return sec?.types?.includes(n?.type);
  });

  // ── Actions ────────────────────────────────────────────────────────────────

  const handleMarkRead = useCallback(async (id) => {
    try {
      await supabase?.from('notifications')?.update({ is_read: true })?.eq('id', id);
      setNotifications(prev => prev?.map(n => n?.id === id ? { ...n, is_read: true } : n));
      refreshCount?.();
    } catch (err) { console.error(err); }
  }, [refreshCount]);

  const handleDelete = useCallback(async (id) => {
    try {
      await supabase?.from('notifications')?.delete()?.eq('id', id);
      setNotifications(prev => prev?.filter(n => n?.id !== id));
      setSelectedIds(prev => prev?.filter(sid => sid !== id));
      refreshCount?.();
    } catch (err) { console.error(err); }
  }, [refreshCount]);

  const handleSelect = (id) => {
    setSelectedIds(prev => prev?.includes(id) ? prev?.filter(sid => sid !== id) : [...prev, id]);
  };

  const handleSelectAll = () => {
    const visibleIds = filtered?.map(n => n?.id);
    const allSelected = visibleIds?.every(id => selectedIds?.includes(id));
    setSelectedIds(allSelected ? [] : visibleIds);
  };

  const handleBulkMarkRead = async () => {
    if (!selectedIds?.length) return;
    setBulkLoading(true);
    try {
      await supabase?.from('notifications')?.update({ is_read: true })?.in('id', selectedIds);
      setNotifications(prev => prev?.map(n => selectedIds?.includes(n?.id) ? { ...n, is_read: true } : n));
      setSelectedIds([]);
      refreshCount?.();
      showSuccess(`${selectedIds?.length} notification(s) marquée(s) comme lue(s)`);
    } catch (err) { console.error(err); } finally { setBulkLoading(false); }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds?.length) return;
    if (!window.confirm(`Supprimer ${selectedIds?.length} notification(s) ?`)) return;
    setBulkLoading(true);
    try {
      await supabase?.from('notifications')?.delete()?.in('id', selectedIds);
      setNotifications(prev => prev?.filter(n => !selectedIds?.includes(n?.id)));
      setSelectedIds([]);
      refreshCount?.();
      showSuccess(`${selectedIds?.length} notification(s) supprimée(s)`);
    } catch (err) { console.error(err); } finally { setBulkLoading(false); }
  };

  const handleMarkAllRead = async () => {
    setBulkLoading(true);
    try {
      let q = supabase?.from('notifications')?.update({ is_read: true })?.eq('is_read', false);
      if (user?.id) q = q?.eq('recipient_id', user?.id);
      await q;
      setNotifications(prev => prev?.map(n => ({ ...n, is_read: true })));
      setSelectedIds([]);
      refreshCount?.();
      showSuccess('Toutes les notifications marquées comme lues');
    } catch (err) { console.error(err); } finally { setBulkLoading(false); }
  };

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const visibleIds = filtered?.map(n => n?.id);
  const allVisibleSelected = visibleIds?.length > 0 && visibleIds?.every(id => selectedIds?.includes(id));
  const showBulkBar = selectedIds?.length > 0;

  return (
    <>
      <Helmet>
        <title>Hub de Notifications – Kapital States</title>
        <meta name="description" content="Hub de notifications en temps réel : rappels de rendez-vous, baisses de prix, mises à jour de demandes et alertes de scoring de leads." />
      </Helmet>
      <div className="min-h-screen bg-surface/30">
        <Header />

        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

          {/* ── Breadcrumb ── */}
          <div className="flex items-center space-x-2 text-xs text-text-secondary mb-5">
            <Link to="/homepage" className="hover:text-brand-earth transition-colors">Accueil</Link>
            <Icon name="ChevronRight" size={12} />
            <span className="text-text-primary font-medium">Hub de Notifications</span>
          </div>

          {/* ── Header ── */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-brand-gold/20 to-brand-earth/20 rounded-2xl flex items-center justify-center">
                  <Icon name="BellRing" size={24} className="text-brand-earth" />
                </div>
                {liveCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none shadow-sm animate-pulse">
                    {liveCount > 99 ? '99+' : liveCount}
                  </span>
                )}
              </div>
              <div>
                <h1 className="text-xl font-bold text-brand-earth leading-tight">Hub de Notifications</h1>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  <p className="text-xs text-text-secondary">
                    {liveCount > 0
                      ? `${liveCount} non lue${liveCount > 1 ? 's' : ''} · Temps réel actif`
                      : 'Tout est à jour · Temps réel actif'}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 self-start sm:self-auto">
              {liveCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  disabled={bulkLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-brand-earth text-white rounded-xl text-sm font-medium hover:bg-brand-earth/90 disabled:opacity-60 transition-all shadow-sm"
                >
                  <Icon name="CheckCheck" size={14} color="white" />
                  <span className="whitespace-nowrap">Tout marquer lu</span>
                </button>
              )}
              <Link
                to="/notification-preferences"
                className="flex items-center gap-2 px-4 py-2 bg-white border border-border text-text-primary rounded-xl text-sm font-medium hover:border-brand-earth/40 hover:text-brand-earth transition-all shadow-sm"
              >
                <Icon name="SlidersHorizontal" size={14} />
                <span className="hidden sm:inline whitespace-nowrap">Préférences</span>
              </Link>
              <Link
                to="/alert-preferences"
                className="flex items-center gap-2 px-4 py-2 bg-white border border-border text-text-primary rounded-xl text-sm font-medium hover:border-brand-earth/40 hover:text-brand-earth transition-all shadow-sm"
              >
                <Icon name="BellPlus" size={14} />
                <span className="hidden sm:inline whitespace-nowrap">Alertes</span>
              </Link>
            </div>
          </div>

          {/* ── Stats Bar ── */}
          {!loading && <StatsBar notifications={notifications} />}

          {/* ── Success Toast ── */}
          {successMsg && (
            <div className="mb-4 flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700 font-medium">
              <Icon name="CheckCircle" size={15} className="text-green-600 flex-shrink-0" />
              {successMsg}
            </div>
          )}

          {/* ── Section Tabs ── */}
          <div className="mb-5 overflow-x-auto pb-1 -mx-1 px-1">
            <div className="flex items-center gap-2 min-w-max">
              {HUB_SECTIONS?.map((sec) => {
                const isActive = activeSection === sec?.id;
                const count = sectionCounts?.[sec?.id] || 0;
                return (
                  <button
                    key={sec?.id}
                    onClick={() => { setActiveSection(sec?.id); setSelectedIds([]); }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 whitespace-nowrap border ${
                      isActive
                        ? `${sec?.activeBg} ${sec?.accent} ${sec?.border} shadow-sm`
                        : 'bg-white border-border text-text-secondary hover:border-brand-earth/30 hover:text-text-primary'
                    }`}
                  >
                    <Icon name={sec?.icon} size={14} strokeWidth={2} />
                    <span>{sec?.label}</span>
                    {count > 0 && (
                      <span className={`min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center leading-none ${
                        isActive ? 'bg-white/80 text-current' : `${sec?.badge} text-white`
                      }`}>
                        {count > 99 ? '99+' : count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Bulk Action Bar ── */}
          {showBulkBar && (
            <div className="mb-4 flex items-center justify-between px-4 py-3 bg-brand-earth/5 border border-brand-earth/20 rounded-2xl">
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSelectAll}
                  className="flex items-center gap-2 text-sm font-medium text-brand-earth hover:text-brand-earth/80 transition-colors"
                >
                  <div className={`w-5 h-5 rounded-md border-2 border-brand-earth flex items-center justify-center ${allVisibleSelected ? 'bg-brand-earth' : 'bg-white'}`}>
                    {allVisibleSelected && <Icon name="Check" size={11} color="white" strokeWidth={3} />}
                  </div>
                  <span>{selectedIds?.length} sélectionnée{selectedIds?.length > 1 ? 's' : ''}</span>
                </button>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleBulkMarkRead}
                  disabled={bulkLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-border rounded-xl text-xs font-medium text-text-primary hover:border-brand-earth/40 hover:text-brand-earth disabled:opacity-60 transition-all"
                >
                  {bulkLoading ? <div className="w-3 h-3 border-2 border-brand-earth border-t-transparent rounded-full animate-spin" /> : <Icon name="CheckCheck" size={13} />}
                  Marquer lues
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={bulkLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-border rounded-xl text-xs font-medium text-text-primary hover:border-red-300 hover:text-red-500 disabled:opacity-60 transition-all"
                >
                  <Icon name="Trash2" size={13} />
                  Supprimer
                </button>
                <button
                  onClick={() => setSelectedIds([])}
                  className="w-7 h-7 rounded-xl bg-white border border-border flex items-center justify-center hover:border-border/80 transition-colors"
                >
                  <Icon name="X" size={13} className="text-text-secondary" />
                </button>
              </div>
            </div>
          )}

          {/* ── Select All Row ── */}
          {!showBulkBar && filtered?.length > 0 && !loading && (
            <div className="flex items-center justify-between mb-3 px-1">
              <button
                onClick={handleSelectAll}
                className="flex items-center gap-2 text-xs text-text-secondary hover:text-text-primary transition-colors"
              >
                <div className={`w-4 h-4 rounded border-2 border-border flex items-center justify-center ${allVisibleSelected ? 'bg-brand-earth border-brand-earth' : 'bg-white'}`}>
                  {allVisibleSelected && <Icon name="Check" size={9} color="white" strokeWidth={3} />}
                </div>
                Tout sélectionner
              </button>
              <span className="text-xs text-text-secondary">
                {filtered?.length} notification{filtered?.length !== 1 ? 's' : ''}
              </span>
            </div>
          )}

          {/* ── Notifications List ── */}
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4]?.map((i) => (
                <div key={i} className="bg-white rounded-2xl border border-border/40 p-4 animate-pulse">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-surface flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-surface rounded-lg w-2/3" />
                      <div className="h-3 bg-surface rounded-lg w-full" />
                      <div className="h-3 bg-surface rounded-lg w-1/3" />
                      <div className="flex gap-2 mt-3 pt-3 border-t border-border/30">
                        <div className="h-7 w-24 bg-surface rounded-lg" />
                        <div className="h-7 w-20 bg-surface rounded-lg" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : filtered?.length === 0 ? (
            <div className="bg-white rounded-2xl border border-border/40 shadow-sm">
              <EmptyState
                iconName="BellOff"
                title="Aucune notification"
                description={
                  activeSection === 'all' ? "Vous n'avez pas encore de notifications. Elles apparaîtront ici en temps réel."
                    : `Aucune notification dans la section "${HUB_SECTIONS?.find(s => s?.id === activeSection)?.label}".`
                }
                ctaLabel={activeSection !== 'all' ? 'Voir toutes les notifications' : undefined}
                ctaIcon={activeSection !== 'all' ? 'LayoutGrid' : undefined}
                ctaVariant="outline"
                onCta={activeSection !== 'all' ? () => setActiveSection('all') : undefined}
                size="lg"
              />
            </div>
          ) : (
            <div className="space-y-3">
              {filtered?.map((notification) => (
                <HubCard
                  key={notification?.id}
                  notification={notification}
                  isSelected={selectedIds?.includes(notification?.id)}
                  onSelect={handleSelect}
                  onMarkRead={handleMarkRead}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}

          {/* ── Footer ── */}
          {!loading && filtered?.length > 0 && (
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-3 pt-6 border-t border-border/50">
              <p className="text-xs text-text-secondary">
                <span className="font-medium text-text-primary">{filtered?.length}</span> notification{filtered?.length !== 1 ? 's' : ''} affichée{filtered?.length !== 1 ? 's' : ''}
              </p>
              <div className="flex items-center gap-4">
                <Link
                  to="/notifications"
                  className="flex items-center gap-1.5 text-sm text-brand-earth font-medium hover:underline"
                >
                  <Icon name="Bell" size={14} />
                  Centre de notifications
                </Link>
                <Link
                  to="/notification-history"
                  className="flex items-center gap-1.5 text-sm text-brand-earth font-medium hover:underline"
                >
                  <Icon name="History" size={14} />
                  Historique complet
                </Link>
              </div>
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default NotificationHub;
